if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));


          // Update both main and inline quantity rules
          this.updateQuantityRules(this.sectionId, html);

          this.updatePromoBannerEntries(variant);
          
          // Update product availability based on variant inventory tracking
          this.updateProductAvailability(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updatePromoBannerEntries(variant) {
        const promoBanner = this.querySelector('.cart-promo-banner');
        if (!promoBanner) return;

        const entriesElement = promoBanner.querySelector('.promo-entries');
        if (!entriesElement) return;

        // Get settings from the promo banner element
        const enableEntries = promoBanner.dataset.enableEntries === 'true';
        if (!enableEntries) return;

        const entriesMultiplier = parseFloat(promoBanner.dataset.entriesValue) || 1;
        const maxEntriesLimit = parseInt(promoBanner.dataset.maxEntriesLimit) || 0;

        if (variant && variant.price) {
          // Calculate entries based on variant price
          let totalEntries = Math.round((variant.price / 100) * entriesMultiplier);

          // Apply max limit if set
          if (maxEntriesLimit > 0 && totalEntries > maxEntriesLimit) {
            totalEntries = maxEntriesLimit;
          }

          // Update the entries display
          entriesElement.textContent = totalEntries + ' entries';
        }
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));

        // Also disable inline buy section
        this.updateInlineAddToCartButton(true, window.variantStrings.unavailable);
      }

      // New method to handle product availability based on inventory tracking
      updateProductAvailability(variant) {
        if (!variant) {
          this.setUnavailable();
          return;
        }

        const isInventoryTracked = variant.inventory_management !== null;
        const isOutOfStock = isInventoryTracked && variant.inventory_quantity <= 0;
        const isAvailable = variant.available;

        if (!isAvailable) {
          // Variant doesn't exist or is not available for sale
          this.setUnavailable();
          return;
        }

        if (isOutOfStock) {
          // Inventory tracked but out of stock
          this.handleOutOfStockVariant();
        } else if (!isInventoryTracked) {
          // Inventory not tracked - show all elements as normal
          this.handleInventoryNotTrackedVariant();
        } else {
          // In stock with inventory tracking - show everything normally
          this.handleInStockVariant();
        }
      }

      handleOutOfStockVariant() {
        // Show price but mark as sold out
        const priceElement = this.querySelector(`#price-${this.dataset.section}`);
        if (priceElement) {
          priceElement.classList.remove('hidden');
        }

        // Hide inventory and quantity rules for out of stock items
        const outOfStockSelectors = ['Inventory', 'Quantity-Rules', 'Volume-Note']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(outOfStockSelectors).forEach((element) => {
          element.classList.add('hidden');
        });

        // Show SKU and volume if they have content
        const skuElement = this.querySelector(`#Sku-${this.dataset.section}`);
        if (skuElement && !skuElement.classList.contains('hidden')) {
          skuElement.classList.remove('hidden');
        }

        const volumeElement = this.querySelector(`#Volume-${this.dataset.section}`);
        if (volumeElement && volumeElement.innerHTML.trim() !== '') {
          volumeElement.classList.remove('hidden');
        }

        // Update buttons to show sold out
        this.productForm?.toggleSubmitButton(true, window.variantStrings.soldOut || 'Sold out');
        this.updateInlineAddToCartButton(true, window.variantStrings.soldOut || 'Sold out');
      }

      handleInventoryNotTrackedVariant() {
        // Show all elements for variants without inventory tracking
        const selectors = ['price', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach((element) => {
          // Only show if the element has content
          if (element.innerHTML.trim() !== '' && !element.querySelector('.hidden')) {
            element.classList.remove('hidden');
          }
        });

        // Hide inventory element since it's not tracked
        const inventoryElement = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (inventoryElement) {
          inventoryElement.classList.add('hidden');
        }

        // Enable add to cart buttons
        this.productForm?.toggleSubmitButton(false, window.variantStrings.addToCart);
        this.updateInlineAddToCartButton(false, window.variantStrings.addToCart);
      }

      handleInStockVariant() {
        // Show all relevant elements for in-stock variants
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach((element) => {
          // Only show if the element has content and is not intentionally hidden
          if (element.innerHTML.trim() !== '' && !element.hasAttribute('data-force-hidden')) {
            element.classList.remove('hidden');
          }
        });

        // Enable add to cart buttons
        this.productForm?.toggleSubmitButton(false, window.variantStrings.addToCart);
        this.updateInlineAddToCartButton(false, window.variantStrings.addToCart);
      }

      // Helper method to update inline add to cart button
      updateInlineAddToCartButton(disabled, text) {
        const inlineAddToCartButton = this.querySelector('.inline-add-to-cart .product-form__submit');
        const inlineAddToCartText = this.querySelector('.inline-add-to-cart .add-to-cart-text');
        
        if (inlineAddToCartButton && inlineAddToCartText) {
          if (disabled) {
            inlineAddToCartButton.setAttribute('disabled', 'disabled');
            inlineAddToCartButton.classList.add('button--disabled');
          } else {
            inlineAddToCartButton.removeAttribute('disabled');
            inlineAddToCartButton.classList.remove('button--disabled');
          }
          inlineAddToCartText.textContent = text || window.variantStrings.addToCart;
        }
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        // Update main quantity form
        if (this.quantityInput && this.quantityForm) {
          this.setQuantityBoundries();

          const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
          
          if (quantityFormUpdated) {
            const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
            for (let selector of selectors) {
              const current = this.quantityForm.querySelector(selector);
              const updated = quantityFormUpdated.querySelector(selector);
              
              if (current && updated) {
                if (selector === '.quantity__input') {
                  const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
                  for (let attribute of attributes) {
                    const valueUpdated = updated.getAttribute(attribute);
                    if (valueUpdated !== null) {
                      current.setAttribute(attribute, valueUpdated);
                    } else {
                      current.removeAttribute(attribute);
                    }
                  }
                } else {
                  current.innerHTML = updated.innerHTML;
                  if (selector === '.quantity__label') {
                    const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
                    if (updatedAriaLabelledBy) {
                      current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                      const labelId = updatedAriaLabelledBy;
                      const currentHiddenLabel = document.getElementById(labelId);
                      const updatedHiddenLabel = html.getElementById(labelId);
                      if (currentHiddenLabel && updatedHiddenLabel) {
                        currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                      }
                    }
                  }
                }
              }
            }
          }
        }

        // Update inline quantity section
        this.updateInlineQuantitySection(sectionId, html);
      }

      updateInlineQuantitySection(sectionId, html) {
        const inlineQuantityInput = this.querySelector('#Quantity-Inline-' + sectionId);
        if (!inlineQuantityInput) return;

        const inlineQuantityUpdated = html.querySelector('#Quantity-Inline-' + sectionId);
        if (!inlineQuantityUpdated) return;

        // Update inline quantity input attributes
        const attributes = ['data-min', 'data-max', 'step', 'min', 'max', 'value'];
        for (let attribute of attributes) {
          const valueUpdated = inlineQuantityUpdated.getAttribute(attribute);
          if (valueUpdated !== null) {
            inlineQuantityInput.setAttribute(attribute, valueUpdated);
          } else {
            inlineQuantityInput.removeAttribute(attribute);
          }
        }

        // Update inline price
        const inlinePrice = this.querySelector('.inline-price');
        const inlinePriceUpdated = html.querySelector('.inline-price');
        if (inlinePrice && inlinePriceUpdated) {
          inlinePrice.innerHTML = inlinePriceUpdated.innerHTML;
        }

        // Update stock availability
        const stockAvailable = this.querySelector('[data-stock-available]');
        const stockAvailableUpdated = html.querySelector('[data-stock-available]');
        if (stockAvailable && stockAvailableUpdated) {
          stockAvailable.innerHTML = stockAvailableUpdated.innerHTML;
          const variantId = stockAvailableUpdated.getAttribute('data-variant-id');
          if (variantId) {
            stockAvailable.setAttribute('data-variant-id', variantId);
          }
        }

        // Update add to cart button based on variant availability
        const variant = this.getSelectedVariant(html);
        if (variant) {
          const isInventoryTracked = variant.inventory_management !== null;
          const isOutOfStock = isInventoryTracked && variant.inventory_quantity <= 0;
          const isAvailable = variant.available;

          const addToCartButton = this.querySelector('.inline-add-to-cart .product-form__submit');
          const addToCartButtonUpdated = html.querySelector('.inline-add-to-cart .product-form__submit');
          const addToCartText = this.querySelector('.inline-add-to-cart .add-to-cart-text');
          
          if (addToCartButton && addToCartText) {
            if (!isAvailable) {
              addToCartButton.setAttribute('disabled', 'disabled');
              addToCartButton.classList.add('button--disabled');
              addToCartText.textContent = window.variantStrings.unavailable;
            } else if (isOutOfStock) {
              addToCartButton.setAttribute('disabled', 'disabled');
              addToCartButton.classList.add('button--disabled');
              addToCartText.textContent = window.variantStrings.soldOut || 'Sold out';
            } else {
              addToCartButton.removeAttribute('disabled');
              addToCartButton.classList.remove('button--disabled');
              addToCartText.textContent = window.variantStrings.addToCart;
            }
          }
        }

        // Update variant ID in inline form
        const inlineVariantInput = this.querySelector('.inline-product-form .product-variant-id');
        const inlineVariantInputUpdated = html.querySelector('.inline-product-form .product-variant-id');
        if (inlineVariantInput && inlineVariantInputUpdated) {
          inlineVariantInput.value = inlineVariantInputUpdated.value;
          const variantId = inlineVariantInputUpdated.getAttribute('data-variant-id');
          if (variantId) {
            inlineVariantInput.setAttribute('data-variant-id', variantId);
          }
          
          if (inlineVariantInputUpdated.hasAttribute('disabled')) {
            inlineVariantInput.setAttribute('disabled', 'disabled');
          } else {
            inlineVariantInput.removeAttribute('disabled');
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}